import { Ban, AlertTriangle, Pill, Copy } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { RxCheck, RxEscalation } from "./RxCheck";

function AvoidList({ avoid }) {
  return (
    <div className="rounded-2xl border-2 border-red-500 bg-red-50 overflow-hidden" data-testid="do-not-prescribe-box">
      <p className="px-5 py-3 bg-red-600 text-white font-heading font-bold flex items-center gap-2"><Ban className="w-5 h-5" /> Do NOT prescribe / use for this patient</p>
      <ul className="divide-y divide-red-200">
        {avoid.map((d, i) => (
          <li key={i} className="px-5 py-3 text-sm" data-testid={`do-not-prescribe-item-${i}`}>
            <p className="font-semibold text-red-800">✕ {d.avoid}</p>
            <p className="text-emerald-800 mt-0.5"><b>Use instead:</b> {d.alternative}</p>
            <p className="text-slate-600 text-xs mt-0.5">{d.reason}{d.source && <span className="text-slate-400"> · {d.source}</span>}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Combos({ combos }) {
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="combo-alerts">
      {combos.map((c, i) => (
        <div key={i} className="text-sm text-amber-900" data-testid={`combo-alert-${i}`}>
          <p className="font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {c.title}</p>
          <p className="mt-0.5">{c.text}</p>
        </div>
      ))}
    </div>
  );
}

function SafeRx({ rx }) {
  const copy = () => navigator.clipboard.writeText(rx.text).then(() => toast.success("Safe prescription copied"));
  return (
    <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="safe-rx-box">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-bold text-emerald-900 flex items-center gap-2 text-sm"><Pill className="w-4 h-4" /> {rx.no_safe?.length ? "Prescribing options — some need a physician's choice" : "Safe options for this patient (if clinically indicated)"}</p>
        <Button size="sm" variant="outline" onClick={copy} data-testid="copy-safe-rx-btn" className="border-emerald-400 text-emerald-800"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy safe prescription</Button>
      </div>
      <ul className="mt-2 text-sm text-emerald-900 space-y-1" data-testid="safe-rx-lines">
        <li><b>Antibiotic:</b> {rx.antibiotic}</li>
        {rx.analgesics.map((a, i) => <li key={i}><b>Analgesic:</b> {a}</li>)}
      </ul>
      <RxEscalation rx={rx} />
      <RxCheck rx={rx} />
      <p className="text-xs text-emerald-800/80 mt-2">Verify doses, allergies and renal/hepatic function before prescribing.</p>
    </div>
  );
}

export function PrescribingAlert({ p }) {
  if (!p) return null;
  return (
    <div className="space-y-3 mb-4" data-testid="prescribing-alert">
      {p.avoid?.length > 0 && <AvoidList avoid={p.avoid} />}
      {p.combos?.length > 0 && <Combos combos={p.combos} />}
      {p.safe_rx && <SafeRx rx={p.safe_rx} />}
    </div>
  );
}
